import type { ReactNode } from "react"
import { forwardRef, memo, useState } from "react"

import type { UserColor } from "@productlane/lib/src/browser"
import type { ProjectIconKeys } from "@productlane/ui"
import { USER_COLOR_PALETTE } from "@productlane/lib/src/browser"
import {
	Button,
	Popover,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@productlane/ui"

import { getProjectIcon } from "@/lib/projects"
import { EmojiPickerPanel } from "./emoji-picker"
import { ColorSection, IconGrid, PICKER_WIDTH } from "./icon-picker-parts"

/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */

export function hexToRGB(h: string, withTailwindOpacity?: "bg" | "border") {
	let r: any = 0,
		g: any = 0,
		b: any = 0

	// 3 digits
	if (h.length == 4) {
		r = "0x" + h[1] + h[1]
		g = "0x" + h[2] + h[2]
		b = "0x" + h[3] + h[3]

		// 6 digits
	} else if (h.length == 7) {
		r = "0x" + h[1] + h[2]
		g = "0x" + h[3] + h[4]
		b = "0x" + h[5] + h[6]
	}

	return `rgb(${+r} ${+g} ${+b}${
		withTailwindOpacity === "border"
			? ` / var(--tw-border-opacity)`
			: withTailwindOpacity === "bg"
				? " / var(--tw-bg-opacity)"
				: ""
	})`
}

export function hexColorToRgbValues(hexColor: string) {
	const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
	hexColor = hexColor.replace(shorthandRegex, function (_m, r, g, b) {
		return r + r + g + g + b + b
	})

	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor)
	return result
		? {
				r: parseInt(result[1]!, 16),
				g: parseInt(result[2]!, 16),
				b: parseInt(result[3]!, 16),
			}
		: null
}

// given a bg color, return a good foreground color
export function textContrastColor(hexColor: string) {
	if (typeof hexColor !== "string") {
		return null
	}
	const colorValues = hexColorToRgbValues(hexColor)
	if (colorValues !== null) {
		const contrastColor =
			colorValues.r * 0.299 + colorValues.g * 0.587 + colorValues.b * 0.114 >
			186
				? "#000000"
				: "#ffffff"
		return contrastColor
	} else {
		return null
	}
}

export function getUserColorFromHex(hex: string): UserColor | null {
	let color: UserColor | null = null
	Object.entries(USER_COLOR_PALETTE).map(([key, value]) => {
		if (value.hex.toLowerCase() === hex.toLowerCase()) {
			color = key as UserColor
		}
	})
	return color
}

// --- Public components ---

/**
 * Color-only picker. Shows a palette row and optional hex input.
 */
interface ColorPickerProps {
	isReadonly?: boolean
	disabled?: boolean
	value: string
	onChange: (color: string) => void
	className?: string
	withInputs?: boolean
	children?: ReactNode
	open?: boolean
	setOpen?: (open: boolean) => void
	// Legacy compat — pass-through to LinearColoredIconPicker
	withIcon?: boolean
	withEmoji?: boolean
	icon?: string | null
	onIconChange?: (icon: string) => void
}

export const ColorPicker = memo(
	forwardRef<HTMLDivElement, ColorPickerProps>(
		(
			{
				isReadonly,
				disabled,
				onChange,
				value,
				withIcon,
				withEmoji,
				icon,
				onIconChange,
				className,
				withInputs: _withInputs = true,
				children,
				open: externalOpen,
				setOpen: externalSetOpen,
			},
			ref,
		) => {
			const [internalOpen, setInternalOpen] = useState(false)
			const open = externalOpen ?? internalOpen
			const setOpen = externalSetOpen ?? setInternalOpen

			// Delegate to LinearColoredIconPicker when withIcon is set
			if (withIcon) {
				return (
					<LinearColoredIconPicker
						ref={ref}
						isReadonly={isReadonly}
						disabled={disabled}
						color={value}
						onColorChange={onChange}
						icon={icon}
						onIconChange={onIconChange}
						withEmoji={withEmoji}
						className={className}
						open={externalOpen}
						setOpen={externalSetOpen}
					>
						{children}
					</LinearColoredIconPicker>
				)
			}

			const trigger = (
				<div className="h-3 w-3 rounded-full" style={{ background: value }} />
			)

			if (isReadonly || disabled) return children ?? trigger

			return (
				<Popover
					align="center"
					open={open}
					onOpenChange={setOpen}
					className="w-auto overflow-hidden p-0 pb-4"
					content={
						<div style={{ width: PICKER_WIDTH }}>
							<ColorSection color={value} onChange={onChange} />
						</div>
					}
					ref={ref}
				>
					{children ? (
						children
					) : (
						<Button variant="outline" className={className}>
							{trigger}
						</Button>
					)}
				</Popover>
			)
		},
	),
)

/**
 * Icon-only picker. Shows a searchable icon grid.
 */
interface LinearIconPickerProps {
	icon: ProjectIconKeys
	onIconChange: (icon: ProjectIconKeys) => void
	color?: string
	className?: string
	disabled?: boolean
	children?: ReactNode
}

export const LinearIconPicker = memo(
	forwardRef<HTMLDivElement, LinearIconPickerProps>(
		(
			{ icon, onIconChange, color: colorProp, className, disabled, children },
			ref,
		) => {
			const color = colorProp ?? "#6B7280"
			const [open, setOpen] = useState(false)
			const Icon = getProjectIcon(icon)

			if (disabled) {
				return (
					children ?? (
						<Button variant="outline" className={className}>
							<Icon className="text-[1rem]" style={{ color }} />
						</Button>
					)
				)
			}

			return (
				<Popover
					align="center"
					className="w-auto overflow-hidden p-0"
					open={open}
					onOpenChange={setOpen}
					content={
						<div style={{ width: PICKER_WIDTH }}>
							<IconGrid
								selectedIcon={icon}
								color={colorProp}
								onPick={(next) => {
									onIconChange(next as ProjectIconKeys)
									setOpen(false)
								}}
							/>
						</div>
					}
					ref={ref}
				>
					{children ? (
						children
					) : (
						<Button variant="outline" className={className}>
							<Icon className="text-[1rem]" style={{ color }} />
						</Button>
					)}
				</Popover>
			)
		},
	),
)

/**
 * Combined color + icon picker. Shows palette, optional hex input, then icon grid.
 */
interface LinearColoredIconPickerProps {
	isReadonly?: boolean
	disabled?: boolean
	color: string
	onColorChange: (color: string) => void
	icon?: string | null
	/** An icon name, or an emoji as `:name:` when `withEmoji` is on. */
	onIconChange?: (icon: string) => void
	/** Adds Linear's Emojis tab; only for icons that Linear also stores as emoji. */
	withEmoji?: boolean
	className?: string
	children?: ReactNode
	open?: boolean
	setOpen?: (open: boolean) => void
}

const TAB_TRIGGER =
	"relative h-auto self-stretch rounded-none px-0 py-0 text-sm font-medium text-tertiary shadow-none ring-0 hover:bg-transparent hover:text-secondary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:ring-0 dark:hover:bg-transparent dark:data-[state=active]:bg-transparent"

// Linear's purple bar under the active tab, drawn by TabsTrigger's own indicator.
const TAB_UNDERLINE = "inset-auto inset-x-0 -bottom-px h-0.5 bg-[#5e6ad2]"

export const LinearColoredIconPicker = memo(
	forwardRef<HTMLDivElement, LinearColoredIconPickerProps>(
		(
			{
				isReadonly,
				disabled,
				color,
				onColorChange,
				icon,
				onIconChange,
				withEmoji,
				className,
				children,
				open: externalOpen,
				setOpen: externalSetOpen,
			},
			ref,
		) => {
			const [internalOpen, setInternalOpen] = useState(false)
			const open = externalOpen ?? internalOpen
			const setOpen = externalSetOpen ?? setInternalOpen
			const [tab, setTab] = useState<"icons" | "emojis">("icons")

			const Icon = getProjectIcon(icon)
			const trigger = <Icon className="text-[1rem]" style={{ color }} />

			if (isReadonly || disabled) return children ?? trigger

			const pick = (next: string) => {
				onIconChange?.(next)
				setOpen(false)
			}
			const iconsPanel = (
				<>
					<ColorSection color={color} onChange={onColorChange} />
					<IconGrid selectedIcon={icon} color={color} onPick={pick} />
				</>
			)

			return (
				<Popover
					align="start"
					className="w-auto overflow-hidden p-0"
					open={open}
					onOpenChange={(next) => {
						setOpen(next)
						if (next) setTab(icon?.startsWith(":") ? "emojis" : "icons")
					}}
					content={
						<div style={{ width: PICKER_WIDTH }}>
							{withEmoji ? (
								<Tabs
									value={tab}
									onValueChange={(value) =>
										setTab(value === "emojis" ? "emojis" : "icons")
									}
								>
									<TabsList
										variant="transparent"
										className="flex h-10 items-stretch gap-6 overflow-visible border-b border-gray-200 px-4 md:overflow-visible dark:border-gray-700/80"
									>
										<TabsTrigger
											value="icons"
											className={TAB_TRIGGER}
											indicatorClassName={TAB_UNDERLINE}
										>
											Icons
										</TabsTrigger>
										<TabsTrigger
											value="emojis"
											className={TAB_TRIGGER}
											indicatorClassName={TAB_UNDERLINE}
										>
											Emojis
										</TabsTrigger>
									</TabsList>
									<TabsContent value="icons">{iconsPanel}</TabsContent>
									<TabsContent value="emojis">
										<EmojiPickerPanel
											columns={10}
											withCustom={false}
											plainSearch
											onPick={(emoji) => pick(`:${emoji.name}:`)}
										/>
									</TabsContent>
								</Tabs>
							) : (
								iconsPanel
							)}
						</div>
					}
					ref={ref}
				>
					{children ? (
						children
					) : (
						<Button variant="outline" className={className}>
							{trigger}
						</Button>
					)}
				</Popover>
			)
		},
	),
)

// Keep old name as alias for backward compatibility
export const IconPicker = LinearIconPicker
